import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import { avatars } from "src/assets/images"
import LeagueFrame from "src/components/LeagueFrame"
import Modal from "src/components/modals/Modal"
import { selectEconomyConfig } from "src/store/common"
import { DEFAULT_AVATAR } from "src/utils/cosmetics"
import { LEAGUE_ICONS, LEAGUE_TONES, formatPoints } from "src/utils/ranking"

import "./PlayerCardModal.css"

// The smallest page the ranking serves: the card needs one row of it, the player's own.
const ONE_PAGE = 10

/* A player's card (owner, 2026-10-09): pressing a player's picture — in the ranking, a lobby seat,
   the result screen, the profile — opens it. A card, not the showcase the classes open (owner, the
   same day): a box of its own, the picture in its league's frame at no more than 240px — the
   avatars are 384–512px pixel art, and blown up to the screen they read as mush — and the name, the
   league, the place, the points and what the league pays a day written under it, never over it.
   The frame carries no league tag: the league is the line under the name. The box is lit in the
   league's colour (`LEAGUE_TONES`).

   The caller hands over what its payload already knows (`user`: id, username, league, points) and
   the `picture` it drew, so the card opens at once with the same face. The place is in no payload
   but the ranking page's, so the card asks `GET /ranking/?around=<id>` for the page that holds the
   player and reads their row — the league and the points from it too, fresher than a lobby
   broadcast. What the league pays a day is the published `daily_credits_by_league` (seam rule 1). */
const PlayerCardModal = ({ user, picture }) => {
  const { t } = useTranslation()
  const economy = useSelector(selectEconomyConfig)
  const [row, setRow] = useState(null)
  const userId = user?.id

  useEffect(() => {
    if (userId == null) return undefined
    let current = true
    apiClient
      .get("/ranking/", { params: { around: userId, per_page: ONE_PAGE } })
      .then((response) => {
        const found = (response.data?.results ?? []).find((entry) => String(entry.id) === String(userId))
        if (current && found) setRow(found)
      })
      .catch(() => {})
    return () => {
      current = false
    }
  }, [userId])

  const league = row?.league ?? user?.league ?? null
  const points = row?.ranking_points ?? user?.rankingPoints ?? user?.ranking_points ?? 0
  const daily = league ? economy?.daily_credits_by_league?.[league] : undefined
  const tone = LEAGUE_TONES[league]

  const stats = [
    { label: t("playerCard.position"), value: row ? `#${row.rank}` : "…" },
    { label: t("playerCard.points"), value: formatPoints(points) },
    ...(daily != null ? [{ label: t("playerCard.dailyCredits"), value: t("playerCard.perDay", { credits: daily }) }] : []),
  ]

  return (
    <Modal width="min(460px, 92vw)">
      <div className="player-card" style={tone ? { "--card-tone": tone } : undefined}>
        <LeagueFrame league={league} size="lg" tag={false} className="player-card-frame">
          <span className="player-card-picture">
            <img src={avatars[picture] || avatars[DEFAULT_AVATAR]} alt="" />
          </span>
        </LeagueFrame>
        <h2 className="player-card-name">{row?.username ?? user?.username ?? ""}</h2>
        <p className="player-card-league">
          {league ? `${LEAGUE_ICONS[league] ?? ""} ${t(`ranking.leagues.${league}`)}`.trim() : t("playerCard.kicker")}
        </p>
        <dl className="player-card-stats">
          {stats.map(({ label, value }) => (
            <div className="player-card-stat" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Modal>
  )
}

export default PlayerCardModal
